import { cn } from "@plasma/utils/cn";
import { Check, ChevronDown } from "lucide-react";
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useState,
} from "react";

import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// Context to share state between Select components
interface SelectContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SelectContext = createContext<SelectContextValue>({
  open: false,
  setOpen: () => {
    // default no-op
  },
});

function Select({
  children,
  value,
  onValueChange,
  defaultValue,
}: {
  children: ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = value ?? internalValue;

  const handleValueChange = (newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue);
    } else {
      setInternalValue(newValue);
    }
    setOpen(false);
  };

  return (
    <SelectContext.Provider
      value={{
        value: currentValue,
        onValueChange: handleValueChange,
        open,
        setOpen,
      }}
    >
      <Popover onOpenChange={setOpen} open={open}>
        {children}
      </Popover>
    </SelectContext.Provider>
  );
}

const SelectGroup = ({ children }: { children: ReactNode }) => (
  <fieldset>{children}</fieldset>
);

function SelectValue({ placeholder }: { placeholder?: string }) {
  const { value } = useContext(SelectContext);
  // SelectValue is rendered inside SelectTrigger, which provides the label context
  // The actual label comes from the SelectItem that matches the current value
  return <span>{value || placeholder}</span>;
}

function SelectTrigger({
  className,
  children,
  ...props
}: ComponentProps<"button">) {
  const { setOpen, open } = useContext(SelectContext);

  return (
    <PopoverTrigger asChild>
      <button
        aria-expanded={open}
        className={cn(
          "border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] focus:outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
          className
        )}
        onClick={() => setOpen(!open)}
        type="button"
        {...props}
      >
        {children}
        <ChevronDown className="size-4 opacity-50" />
      </button>
    </PopoverTrigger>
  );
}

function SelectContent({
  className,
  children,
  position: _position,
  ...props
}: ComponentProps<"div"> & { position?: string }) {
  return (
    <PopoverContent
      className={cn("w-[var(--radix-popover-trigger-width)] p-1", className)}
      {...props}
    >
      {children}
    </PopoverContent>
  );
}

function SelectItem({
  className,
  children,
  value: itemValue,
  ...props
}: ComponentProps<"button"> & { value: string }) {
  const { value, onValueChange } = useContext(SelectContext);
  const isSelected = value === itemValue;

  return (
    <button
      className={cn(
        "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none select-none",
        isSelected && "bg-accent/50",
        className
      )}
      onClick={() => onValueChange?.(itemValue)}
      type="button"
      {...props}
    >
      <span className="absolute left-2 flex size-3.5 items-center justify-center">
        {isSelected && <Check className="size-4" />}
      </span>
      {children}
    </button>
  );
}

function SelectLabel({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("py-1.5 pr-2 pl-8 text-sm font-semibold", className)}
      {...props}
    />
  );
}

function SelectSeparator({ className, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("bg-muted -mx-1 my-1 h-px", className)} {...props} />
  );
}

// Compatibility exports
const SelectScrollUpButton = () => null;
const SelectScrollDownButton = () => null;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
